import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, User, Mail, Phone, Shield } from 'lucide-react';
import { accountFormSchema, AccountFormData } from '../schemas/account';
import { accountService } from '../api/accountRepo';
import { staffApi } from '../api/mockRepo';
import { Staff } from '../types';
import { getRoleDisplayName } from '../utils';
import { canManageStaffDB } from '../../../lib/rbac';
import { useStore } from '../../../lib/store';
import toast from 'react-hot-toast';

const AccountEditor: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useStore();
  const [availableStaff, setAvailableStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    account: any;
    tempPassword?: string;
    inviteToken?: string;
  } | null>(null);
  const [canManageAccounts, setCanManageAccounts] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    checkPermissions();
  }, [user]);

  const checkPermissions = async () => {
    if (!user?.id) {
      setCanManageAccounts(false);
      setCheckingPermissions(false);
      return;
    }

    try {
      const canManage = await canManageStaffDB(user.id);
      setCanManageAccounts(canManage);
    } catch (error) {
      console.error('Error checking permissions:', error);
      setCanManageAccounts(false);
    } finally {
      setCheckingPermissions(false);
    }
  };

  if (checkingPermissions) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canManageAccounts) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500">Access denied. Admin privileges required.</div>
      </div>
    );
  }

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<AccountFormData>({
    resolver: zodResolver(accountFormSchema),
    defaultValues: {
      method: 'invite'
    }
  });

  const selectedStaffId = watch('staffId');

  useEffect(() => {
    loadAvailableStaff();
  }, []);

  useEffect(() => {
    if (selectedStaffId) {
      const staff = availableStaff.find(s => s.id === selectedStaffId);
      if (staff) {
        setValue('email', staff.email || `${staff.empCode.toLowerCase()}@medsystem.com`);
        setValue('phone', staff.phone || '');
        setValue('role', staff.role);
      }
    }
  }, [selectedStaffId, availableStaff, setValue]);

  const loadAvailableStaff = async () => {
    try {
      const allStaff = await staffApi.getAll();
      const accounts = await accountService.list();
      const staffWithAccounts = new Set(accounts.map(acc => acc.staffId));
      
      const available = allStaff.filter(staff => 
        !staffWithAccounts.has(staff.id) && staff.status === 'active'
      );
      
      setAvailableStaff(available);
    } catch (error) {
      toast.error('Failed to load staff members');
      console.error('Error loading staff:', error);
    }
  };

  const onSubmit = async (data: AccountFormData) => {
    try {
      setLoading(true);
      const result = await accountService.create(data);
      setResult(result);
      toast.success('Account created successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create account');
      console.error('Error creating account:', error);
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    'doctor', 'nurse', 'pharmacist', 'lab_tech',
    'reception', 'billing', 'cleaner', 'security', 'admin', 'other'
  ];

  if (result) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/staff/accounts')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Account Created</h1>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900">Account Created Successfully</h3>
              <p className="text-gray-600">Email: {result.account.email}</p>
            </div>

            {result.tempPassword && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800">Temporary Password</h4>
                <p className="text-yellow-700 font-mono">{result.tempPassword}</p>
                <p className="text-sm text-yellow-600 mt-2">
                  Please share this password securely with the staff member.
                </p>
              </div>
            )}

            {result.inviteToken && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800">Invitation Link</h4>
                <p className="text-blue-700 font-mono break-all">
                  {window.location.origin}/invite/{result.inviteToken}
                </p>
                <p className="text-sm text-blue-600 mt-2">
                  Send this link to the staff member to complete account setup.
                </p>
              </div>
            )}

            <div className="flex space-x-3 justify-center">
              <button
                onClick={() => setResult(null)}
                className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
              >
                Create Another
              </button>
              <button
                onClick={() => navigate('/staff/accounts')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Back to Accounts
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/staff/accounts')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Account</h1>
          <p className="text-gray-600">Create a login account for a staff member</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Account Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Staff Member
              </label>
              <select
                {...register('staffId')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select staff member...</option>
                {availableStaff.map(staff => (
                  <option key={staff.id} value={staff.id}>
                    {staff.name} ({staff.empCode}) - {getRoleDisplayName(staff.role)}
                  </option>
                ))}
              </select>
              {errors.staffId && (
                <p className="mt-1 text-sm text-red-600">{errors.staffId.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email Address
              </label>
              <input
                type="email"
                {...register('email')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone (Optional)
              </label>
              <input
                type="tel"
                {...register('phone')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Shield className="w-4 h-4 inline mr-1" />
                Role
              </label>
              <select
                {...register('role')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {roles.map(role => (
                  <option key={role} value={role}>
                    {getRoleDisplayName(role)}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account Setup Method
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    {...register('method')}
                    value="invite"
                    className="mr-2"
                  />
                  Send invitation link (recommended)
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    {...register('method')}
                    value="password"
                    className="mr-2"
                  />
                  Set temporary password
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/staff/accounts')}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Account'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountEditor;
